import {
  Component,
  Input,
  OnDestroy,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from "@angular/core";
import { SentryErrorHandler } from "../../../shared/error-handler/sentry-error-handler";
import { Subscription } from "rxjs";
import { NotifyService } from "../../../notify/notify.service";
import { QuoteRepository } from "../../quote.repository";
import { eventType } from "../../../notify/event.const";
import { TOAST } from "../../../notify/toast/toast-type.enum";
import { BackendErrorPipe } from "../../../shared/backend/backend-error.pipe";
import { Quote } from "../../quote.type";
import { quoteStatus } from "../quote-status.const";
import { role } from "../../../user/role.const";
import { noop } from "../../../shared/noop.function";
import { quoteFormTabs } from "../quote-form-tabs.const";
import { CurrencyPipe } from "../../../shared/currencies/currency.pipe";
import { EventService } from "../../../shared/event/event.service";
import { BaseLclQuote } from "../../models/quote-base-lcl.model";
import { quoteSource } from "../quote-source.const";
import { LtlQuote } from "app/quote/models/quote-ltl.model";
import { FclQuote } from "app/quote/models/quote-fcl.model";
import { DownloadService } from "app/shared/download/download.service";
import { MultiQuote } from "app/quote/models/quote-multi.model";
import { Bind } from "primeng/bind";
import { ButtonDirective } from "primeng/button";
import { Popover } from "primeng/popover";
import { PopoverArrowDirective } from "app/ui/popover/popover-arrow.directive";
import { NgxPermissionsModule } from "ngx-permissions";
import { QuoteDeclineFormComponent } from "../../quote-decline-form/quote-decline-form.component";

@Component({
  selector: "app-quote-form-actions",
  templateUrl: "./quote-form-actions.component.html",
  styleUrls: ["./quote-form-actions.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    Bind,
    Popover,
    PopoverArrowDirective,
    NgxPermissionsModule,
    QuoteDeclineFormComponent,
    CurrencyPipe,
  ],
})
export class QuoteFormActionsComponent implements OnDestroy {
  private quoteRepository = inject(QuoteRepository);
  private notifyService = inject(NotifyService);
  private eventService = inject(EventService);
  private downloadService = inject(DownloadService);

  @Input() quote: Quote;
  readonly activeTab = input<string>(undefined);
  readonly isMailFormValid = input<boolean>(undefined);
  readonly isNextTabPossible = input<boolean>(true);
  readonly quoteSent = output<Quote>();
  readonly quoteDeleted = output<Quote>();
  readonly quoteReplicated = output<Quote>();
  readonly quoteAccepted = output<Quote>();
  readonly quoteDeclined = output<Quote>();
  readonly quoteClosed = output<Quote>();
  readonly nextTab = output();

  public loading = false;
  public quoteStatus = quoteStatus;
  public role = role;
  public tabs = quoteFormTabs;
  public subscription = new Subscription();
  public quoteSource = quoteSource;
  public declineFormVisible = false;
  private sentry = inject(SentryErrorHandler);

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public sendQuote(): void {
    if (this.loading) {
      return;
    }

    const confirmationMessage =
      this.quote.getClients().length === 1
        ? "Are you sure you want to send quotation?"
        : `This quote will be sent to each of ${
            this.quote.getClients().length
          } selected clients. Are you sure you want to proceed?`;

    this.subscription.add(
      this.notifyService.confirm(confirmationMessage, "Send").subscribe({
        next: () => {
          this.loading = true;
          this.quote.setStatus(this.quoteStatus.pending);
          this.quoteRepository
            .sendQuote(
              this.quote.getId(),
              this.quote.getEmail()?.getMessageId(),
              this.quote.getEmail()?.getReceivedAt(),
            )
            .subscribe({
              next: (success) => {
                this.loading = false;
                this.notifyService.toast(TOAST.INFO, "Quote has been sent");
                this.eventService.eventBus.emit({
                  event: eventType.quoteSent,
                  data: this.quote,
                });
                this.quoteSent.emit(this.quote);
              },
              error: (err) => {
                this.loading = false;
                const error = new BackendErrorPipe().transform(err, "string");
                this.sentry.handleError(error, { quoteOnSend: this.quote });
                this.notifyService.toast(
                  TOAST.ERROR,
                  "Quote has not been sent",
                  error,
                );
              },
            });
        },
      }),
    );
  }

  public deleteQuote(): void {
    this.subscription.add(
      this.notifyService
        .confirm("Are you sure you want to delete quotation?", "Delete", {
          destructive: true,
        })
        .subscribe({
          next: () => {
            this.quoteRepository.deleteQuote(this.quote.getId()).subscribe({
              next: (deletedQuote) => {
                this.notifyService.toast(
                  TOAST.SUCCESS,
                  "Quote has been deleted",
                );
                this.quoteDeleted.emit(this.quote);
              },
              error: (err) => {
                this.sentry.handleError(err, { quoteOnDelete: this.quote });
                this.notifyService.toast(TOAST.ERROR, "Error");
              },
            });
          },
          error: noop,
        }),
    );
  }

  public replicateQuote(keepTotalPrice = false): void {
    this.loading = true;
    this.subscription.add(
      this.quoteRepository
        .replicateQuote(this.quote.getId(), keepTotalPrice)
        .subscribe({
          next: (updatedQuote) => {
            this.notifyService.toast(
              TOAST.SUCCESS,
              "Quote has been replicated",
            );
            this.quoteReplicated.emit(updatedQuote);
            this.loading = false;
          },
          error: (err) => {
            this.sentry.handleError(err, { quoteonUpdate: this.quote });
            this.notifyService.toast(TOAST.ERROR, "Error");
            this.loading = false;
          },
        }),
    );
  }

  public negotiateQuote(): void {
    this.loading = true;
    this.subscription.add(
      this.quoteRepository.negotiateQuote(this.quote.getId()).subscribe({
        next: (updatedQuote) => {
          this.notifyService.toast(TOAST.SUCCESS, "Quote has been replicated");
          this.quoteReplicated.emit(updatedQuote);
          this.loading = false;
        },
        error: (err) => {
          this.sentry.handleError(err, { quoteonUpdate: this.quote });
          this.notifyService.toast(TOAST.ERROR, "Error");
          this.loading = false;
        },
      }),
    );
  }

  public acceptQuote(): void {
    if (this.quote.getStatus() === this.quoteStatus.negotiated) {
      this.subscription.add(
        this.notifyService
          .confirm(
            "Accepting a quote with Negotiated status may lead to incorrect calculation of the Acceptance ratio, please consider accepting the latest SENT version instead",
            "Accept Negotiated Quote",
          )
          .subscribe({
            next: () => this.doAcceptQuote(),
            error: noop,
          }),
      );
      return;
    }

    this.doAcceptQuote();
  }

  private doAcceptQuote(): void {
    this.loading = true;
    this.subscription.add(
      this.quoteRepository.acceptQuote(this.quote.getId()).subscribe({
        next: (acceptedQuote) => {
          this.notifyService.toast(TOAST.SUCCESS, "Quote has been accepted");
          this.eventService.eventBus.emit({
            event: eventType.qouteAccepted,
            data: acceptedQuote,
          });
          this.quoteAccepted.emit(acceptedQuote);
          this.loading = false;
        },
        error: (err) => {
          this.sentry.handleError(err, { quoteOnAccept: this.quote });
          this.notifyService.toast(TOAST.ERROR, "Error");
          this.loading = false;
        },
      }),
    );
  }

  public unacceptQuote(): void {
    this.loading = true;
    this.subscription.add(
      this.quoteRepository.unacceptQuote(this.quote.getId()).subscribe({
        next: (acceptedQuote) => {
          this.notifyService.toast(TOAST.SUCCESS, "Quote has been unaccepted");
          this.eventService.eventBus.emit({
            event: eventType.qouteUnaccepted,
            data: acceptedQuote,
          });
          this.quoteAccepted.emit(acceptedQuote);
          this.loading = false;
        },
        error: (err) => {
          this.sentry.handleError(err, { quoteOnAccept: this.quote });
          this.notifyService.toast(TOAST.ERROR, "Error");
          this.loading = false;
        },
      }),
    );
  }

  public declineQuote(reasonOfDecline: string): void {
    this.loading = true;
    this.subscription.add(
      this.quoteRepository
        .declineQuote(this.quote.getId(), reasonOfDecline)
        .subscribe({
          next: (declinedQuote) => {
            this.notifyService.toast(TOAST.SUCCESS, "Quote has been declined");
            this.eventService.eventBus.emit({
              event: eventType.qouteDeclined,
              data: declinedQuote,
            });
            this.quoteDeclined.emit(declinedQuote);
            this.loading = false;
          },
          error: (err) => {
            this.sentry.handleError(err, { quoteOnAccept: this.quote });
            this.notifyService.toast(TOAST.ERROR, "Error");
            this.loading = false;
          },
        }),
    );
  }

  public undeclineQuote(): void {
    this.loading = true;
    this.subscription.add(
      this.quoteRepository.undeclineQuote(this.quote.getId()).subscribe({
        next: (declinedQuote) => {
          this.notifyService.toast(TOAST.SUCCESS, "Quote has been undeclined");
          this.eventService.eventBus.emit({
            event: eventType.qouteDeclined,
            data: declinedQuote,
          });
          this.quoteDeclined.emit(declinedQuote);
          this.loading = false;
        },
        error: (err) => {
          this.sentry.handleError(err, { quoteOnAccept: this.quote });
          this.notifyService.toast(TOAST.ERROR, "Error");
          this.loading = false;
        },
      }),
    );
  }

  public downloadQuote() {
    this.quoteRepository.downloadFile(this.quote).subscribe((blob) => {
      let fileName = this.quote.getFileName();
      fileName = fileName.substring(fileName.lastIndexOf("/") + 1);
      fileName = decodeURI(fileName);
      this.downloadService.downloadFile(blob, fileName);
    });
  }

  public getAverageMarginPercent() {
    if (this.quote instanceof BaseLclQuote) {
      return this.quote.getAverageMarginPercent();
    }

    return null;
  }

  public getAverageMargin() {
    return this.quote instanceof LtlQuote && this.quote.getAverageMarginEuro()
      ? this.quote.getAverageMarginEuro()
      : this.quote.getAverageMargin();
  }

  public getAverageMarginText() {
    if (this.quote instanceof LtlQuote && this.quote.getAverageMarginEuro()) {
      return (
        new CurrencyPipe().transform(this.quote.getAverageMarginEuro()) + "€"
      );
    } else {
      return "$" + new CurrencyPipe().transform(this.quote.getAverageMargin());
    }
  }

  public isExtendAvailable() {
    return this.quote instanceof FclQuote;
  }

  public emitNextTab() {
    this.nextTab.emit();
  }

  public emitClose() {
    this.quoteClosed.emit(this.quote);
  }

  public isAvarageMarginAvailable(): boolean {
    return this.quote instanceof MultiQuote ? false : true;
  }
}
